import React, { Component } from 'react';
import axios from 'axios';
import pickBy from 'lodash/pickBy';
import classNames from 'classnames';
import { array, arrayOf, bool, func, number, object, shape, string } from 'prop-types';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import { apiBaseUrl } from '../../../util/api';
import { propTypes } from '../../../util/types';
import { parse, stringify } from '../../../util/urlHelpers';
import { FormattedMessage, intlShape, useIntl } from '../../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../../util/search';
import { createResourceLocatorString, matchPathname, pathByRouteName } from '../../../util/routes';

import {
  H3,
  Modal,
  Button,
  NamedLink,
  LinkedLogo,
  PrimaryButton,
  LimitedAccessBanner,
  PrimaryButtonInline,
  SecondaryButtonInline,
  ModalMissingInformation,
  UserNativeLang,
} from '../../../components';

import MenuIcon from './MenuIcon';
import SearchIcon from './SearchIcon';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import SearchRequirementsForm from '../../SearchPage/SearchRequirementsForm/SearchRequirementsForm';

import css from './Topbar.module.css';
import { ReactComponent as SearchWhiteSVG } from '../../../assets/svg/search-white.svg';
import { isReactNativeWrappedApp } from '../../../util/reactnativeAppHelper';

const MAX_MOBILE_SCREEN_WIDTH = 1024;

const redirectToURLWithModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  // Note: sort order is stable in JS
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};
// Returns links in order where primary links are returned first
const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.sort(compareGroups);
};

// Resolves in-app links against route configuration
const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.map(linkConfig => {
    const { type, href } = linkConfig;
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      // Internal link
      const testURL = new URL('http://my.marketplace.com' + href);
      const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
      if (matchedRoutes.length > 0) {
        const found = matchedRoutes[0];
        const to = { search: testURL.search, hash: testURL.hash };
        return {
          ...linkConfig,
          route: {
            name: found.route?.name,
            params: found.params,
            to,
          },
        };
      }
    }
    return linkConfig;
  });
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName ? cmsPageName : inboxPageName ? inboxPageName : `${found.route?.name}`;
  }
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });

  return (
    <div className={classes}>
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  );
};

GenericError.propTypes = {
  show: bool.isRequired,
};

class TopbarComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isModalOpen: false,
      contactLoading: false,
      isSubmissionSuccessful: false,
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleLogout = this.handleLogout.bind(this);
    this.handleMobileMenuOpen = this.handleMobileMenuOpen.bind(this);
    this.handleMobileMenuClose = this.handleMobileMenuClose.bind(this);
    this.handleMobileSearchOpen = this.handleMobileSearchOpen.bind(this);
    this.handleMobileSearchClose = this.handleMobileSearchClose.bind(this);
    this.handleSubmitRequirements = this.handleSubmitRequirements.bind(this);
  }

  handleMobileMenuOpen() {
    redirectToURLWithModalState(this.props, 'mobilemenu');
  }

  handleMobileMenuClose() {
    redirectToURLWithoutModalState(this.props, 'mobilemenu');
  }

  handleMobileSearchOpen() {
    // redirectToURLWithModalState(this.props, 'mobilesearch');

    const { history, routeConfiguration } = this.props;
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, {}));
  }

  handleMobileSearchClose() {
    redirectToURLWithoutModalState(this.props, 'mobilesearch');
  }

  handleSubmit(values) {
    const { currentSearchParams } = this.props;
    const { location, keywords, ...rest } = values || {};
    const { history, config, routeConfiguration } = this.props;

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords };
      }

      // topbar search defaults to 'location' search
      const { search, selectedPlace } = location;
      const { origin, bounds } = selectedPlace;
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        bounds,
        address: search,
        ...originMaybe,
        keywords,
        ...rest,
      };
    };

    const searchParams = {
      ...currentSearchParams,
      ...topbarSearchParams(),
    };

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams));
  }

  handleLogout() {
    const { onLogout, history, routeConfiguration } = this.props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }
    });
  }

  handleSubmitRequirements(values) {
    this.setState(prevState => ({
      ...prevState,
      contactLoading: true,
    }));

    const formData = new FormData();
    formData.append('name', values?.name);
    formData.append('email', values?.email);
    formData.append('requirements', values?.requirements);
    formData.append('phoneNumber', values?.phoneNumber?.number);
    formData.append('phoneDialCode', values?.phoneNumber?.dialCode);

    // Append all files to the formData object
    for (let i = 0; i < values.additionalDocuments?.length; i++) {
      formData.append('files', values.additionalDocuments?.[i]);
    }

    axios
      .post(`${apiBaseUrl()}/api/sendRequirementsMail`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(res => {
        // console.log({ res });
        this.setState(prevState => ({
          ...prevState,
          contactLoading: false,
          isSubmissionSuccessful: true,
        }));
      })
      .catch(err => {
        console.error({ err });
        this.setState(prevState => ({
          ...prevState,
          contactLoading: false,
        }));
      });
  }

  render() {
    const {
      className,
      rootClassName,
      desktopClassName,
      mobileRootClassName,
      mobileClassName,
      isAuthenticated,
      isLoggedInAs,
      authScopes,
      authInProgress,
      currentUser,
      currentUserHasListings,
      currentUserHasOrders,
      currentPage,
      notificationCount,
      intl,
      location,
      onManageDisableScrolling,
      onResendVerificationEmail,
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
      showGenericError,
      config,
      routeConfiguration,
      notShowLowerSection,
      history,
      showRequirementsButton,
      userNativeLang,
    } = this.props;
    const isVendor = currentUser?.attributes?.profile?.publicData?.customUserType === 'vendor';
    const { mobilemenu, mobilesearch, keywords, address, origin, bounds } = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });

    // Custom links are sorted so that group="primary" are always at the beginning of the list.
    const sortedCustomLinks = sortCustomLinks(config.topbar?.customLinks);
    const customLinks = getResolvedCustomLinks(sortedCustomLinks, routeConfiguration);
    const resolvedCurrentPage = currentPage || getResolvedCurrentPage(location, routeConfiguration);

    const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
    const isMobileLayout = hasMatchMedia
      ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
      : true;

    const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';
    const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';
    const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

    const mobileMenu = (
      <TopbarMobileMenu
        isAuthenticated={isAuthenticated}
        currentUserHasListings={currentUserHasListings}
        currentUser={currentUser}
        onLogout={this.handleLogout}
        notificationCount={notificationCount}
        currentPage={resolvedCurrentPage}
        customLinks={customLinks}
        userNativeLang={userNativeLang}
      />
    );

    const topbarSearcInitialValues = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords };
      }

      // Only render current search if full place object is available in the URL params
      const locationFieldsPresent = isOriginInUse(config)
        ? address && origin && bounds
        : address && bounds;
      return {
        keywords,
        location: locationFieldsPresent
          ? {
              search: address,
              selectedPlace: { address, origin, bounds },
            }
          : null,
      };
    };

    const initialSearchFormValues = topbarSearcInitialValues();
    const classes = classNames(rootClassName || css.root, className);
    const listingButtonLabel = intl.formatMessage({
      id: isVendor ? 'Topbar.listYourProductsButtonText' : 'Topbar.postForQuotesButtonText',
    });

    return (
      <div className={classes}>
        <LimitedAccessBanner
          isAuthenticated={isAuthenticated}
          isLoggedInAs={isLoggedInAs}
          authScopes={authScopes}
          currentUser={currentUser}
          onLogout={this.handleLogout}
          currentPage={resolvedCurrentPage}
        />

        <div
          className={classNames(mobileRootClassName || css.container, mobileClassName, {
            [css.appContainer]: isReactNativeWrappedApp(),
          })}
        >
          {isReactNativeWrappedApp() ? null : (
            <Button
              rootClassName={css.menu}
              onClick={this.handleMobileMenuOpen}
              title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
            >
              <MenuIcon className={css.menuIcon} />
              {notificationDot}
            </Button>
          )}

          <LinkedLogo
            layout={'mobile'}
            alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
            linkToExternalSite={config?.topbar?.logoLink}
            logoClassName={css.logo}
          />
          <div className={css.mobileMenuLangContainer}>
            <UserNativeLang name="topbarMobileNativeLang" />

            <Button
              rootClassName={css.searchMenu}
              onClick={this.handleMobileSearchOpen}
              title={intl.formatMessage({ id: 'Topbar.searchIcon' })}
            >
              <SearchWhiteSVG />
              {/* <SearchIcon className={css.searchMenuIcon} /> */}
            </Button>
          </div>
        </div>

        <div className={css.desktop}>
          <TopbarDesktop
            className={desktopClassName}
            currentUserHasListings={currentUserHasListings}
            currentUser={currentUser}
            currentPage={resolvedCurrentPage}
            initialSearchFormValues={initialSearchFormValues}
            intl={intl}
            isAuthenticated={isAuthenticated}
            notificationCount={notificationCount}
            onLogout={this.handleLogout}
            onSearchSubmit={this.handleSubmit}
            config={config}
            customLinks={customLinks}
            history={history}
            userNativeLang={userNativeLang}
          />
        </div>

        <Modal
          usePortal
          id="TopbarMobileMenu"
          isOpen={isMobileMenuOpen}
          onClose={this.handleMobileMenuClose}
          containerClassName={css.modalContainer}
          onManageDisableScrolling={onManageDisableScrolling}
          showCloseBtn={!isReactNativeWrappedApp()}
        >
          {authInProgress ? null : mobileMenu}
        </Modal>

        <Modal
          usePortal
          id="TopbarMobileSearch"
          isOpen={isMobileSearchOpen}
          onClose={this.handleMobileSearchClose}
          containerClassName={css.modalContainerSearchForm}
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.searchContainer}>
            <TopbarSearchForm
              className={css.searchLink}
              isMobile
              history={history}
              appConfig={config}
              routes={routeConfiguration}
              onSubmit={this.handleSubmit}
              initialValues={initialSearchFormValues}
            />

            <p className={css.mobileHelp}>
              <FormattedMessage id="Topbar.mobileSearchHelp" />
            </p>
          </div>
        </Modal>

        <ModalMissingInformation
          id="MissingInformationReminder"
          containerClassName={css.missingInformationModal}
          currentUser={currentUser}
          currentUserHasListings={currentUserHasListings}
          currentUserHasOrders={currentUserHasOrders}
          location={location}
          onManageDisableScrolling={onManageDisableScrolling}
          onResendVerificationEmail={onResendVerificationEmail}
          sendVerificationEmailInProgress={sendVerificationEmailInProgress}
          sendVerificationEmailError={sendVerificationEmailError}
        />

        <GenericError show={showGenericError} />

        <section className={css.subBarRoot}>
          {!currentUser ? (
            <div className={css.clickbaitContainer}>
              {!currentUser && (
                <PrimaryButtonInline type="button" className={css.namedLinkButton}>
                  <NamedLink name="SignupAsVendorPage">
                    <FormattedMessage id="Topbar.listYourProductsButtonText" />
                  </NamedLink>
                </PrimaryButtonInline>
              )}
              {!currentUser && (
                <PrimaryButtonInline type="button" className={css.namedLinkButton}>
                  <NamedLink name="SignupAsCustomerPage">
                    <FormattedMessage id="Topbar.postForQuotesButtonText" />
                  </NamedLink>
                </PrimaryButtonInline>
              )}
            </div>
          ) : (
            <div className={css.singlePostButtonContainer}>
              <PrimaryButton type="button" className={css.namedLinkButton}>
                <NamedLink name="NewListingPage">{listingButtonLabel}</NamedLink>
              </PrimaryButton>
            </div>
          )}

          {showRequirementsButton && (
            <SecondaryButtonInline
              onClick={e => {
                this.setState(prevState => ({
                  ...prevState,
                  isModalOpen: true,
                }));
              }}
              className={css.requirementsFormButton}
            >
              <FormattedMessage id="Topbar.submitRequirementsButtonText" />
            </SecondaryButtonInline>
          )}

          <Modal
            usePortal
            id="OrderFormInModal"
            showAsModalMaxWidth={279}
            isOpen={this.state.isModalOpen}
            onManageDisableScrolling={onManageDisableScrolling}
            onClose={e => {
              e.stopPropagation();
              this.setState(prevState => ({
                ...prevState,
                isModalOpen: false,
              }));
            }}
          >
            <SearchRequirementsForm
              onSubmit={this.handleSubmitRequirements}
              contactLoading={this.state.contactLoading}
            />
          </Modal>

          <div>
            <Modal
              usePortal
              id="RequirementsSubmissionModal"
              showAsModalMaxWidth={279}
              isOpen={this.state.isSubmissionSuccessful}
              onManageDisableScrolling={onManageDisableScrolling}
              onClose={e => {
                e.stopPropagation();
                this.setState(prevState => ({
                  ...prevState,
                  isSubmissionSuccessful: false,
                }));
              }}
            >
              <div>
                <H3>
                  <FormattedMessage id="Topbar.requirementSubmission.title" />
                </H3>
                <p>
                  <FormattedMessage id="Topbar.requirementSubmission.desc" />
                </p>
              </div>
            </Modal>
          </div>
        </section>
      </div>
    );
  }
}

TopbarComponent.defaultProps = {
  className: null,
  rootClassName: null,
  desktopClassName: null,
  mobileRootClassName: null,
  mobileClassName: null,
  notificationCount: 0,
  currentUser: null,
  currentUserHasOrders: null,
  currentPage: null,
  sendVerificationEmailError: null,
  authScopes: [],
  showRequirementsButton: false,
};

TopbarComponent.propTypes = {
  className: string,
  rootClassName: string,
  desktopClassName: string,
  mobileRootClassName: string,
  mobileClassName: string,
  isAuthenticated: bool.isRequired,
  isLoggedInAs: bool.isRequired,
  authScopes: array,
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentUserHasListings: bool.isRequired,
  currentUserHasOrders: bool,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  onResendVerificationEmail: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  showGenericError: bool.isRequired,
  showRequirementsButton: bool,

  // These are passed from Page to keep Topbar rendering aware of location changes
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const Topbar = props => {
  const intl = useIntl();
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();

  return (
    <TopbarComponent
      routeConfiguration={routeConfiguration}
      config={config}
      intl={intl}
      {...props}
    />
  );
};

export default Topbar;
